<template>
  <div class="min-h-screen bg-gray-50 px-4 py-6 pb-28" dir="rtl">
    <div class="max-w-2xl mx-auto space-y-6">
      
      <!-- Header Card -->
      <div class="bg-gradient-to-r from-blue-600 to-blue-700 rounded-xl p-6 text-white">
        <div class="flex items-center gap-3">
          <div class="w-14 h-12 rounded-full bg-white bg-opacity-20 backdrop-blur-sm flex items-center justify-center">
            <svg class="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
          </div>
          <div class="w-full">
            <h2 class="text-xl font-bold">{{ request_info.RequestName }}</h2>
            <p class="text-sm text-blue-50">{{ $route.params.rec_name }}</p>
          </div>
        </div>
      </div>

      <!-- Request Info Card -->
      <div class="bg-white rounded-xl border border-gray-200 p-6 space-y-4">
        
        <!-- Last Status -->
        <div class="flex items-center justify-between p-4 bg-blue-50 rounded-lg border border-blue-200">
          <div class="flex items-center gap-3">
            <div class="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
              <svg class="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <div class="w-full">
              <label class="text-xs text-gray-600 block">{{ $t('requestStatusQ') }}</label>
              <span class="text-sm font-bold text-gray-900">{{ request_info.LastStatus }}</span>
            </div>
          </div>
        </div>

        <!-- Request Details -->
        <div class="space-y-3">
          <div class="flex items-center gap-3">
            <div class="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center flex-shrink-0">
              <svg class="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
              </svg>
            </div>
            <div class="w-full">
              <label class="text-xs text-gray-600">{{ $t('expertReceiptQ') }}</label>
              <p class="text-sm font-medium text-gray-900">{{ request_info.ExpertName }}</p>
            </div>
          </div>

          <div v-if="request_info.DevicePass" class="flex items-center gap-3">
            <div class="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center flex-shrink-0">
              <svg class="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
              </svg>
            </div>
            <div class="w-full">
              <label class="text-xs text-gray-600">{{ $t('devicePasswordQ') }}</label>
              <p class="text-sm font-medium text-gray-900">{{ request_info.DevicePass }}</p>
            </div>
          </div>

          <div v-if="request_info.AppearanceProblem" class="flex items-start gap-3">
            <div class="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center flex-shrink-0">
              <svg class="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 21a4 4 0 01-4-4V5a2 2 0 012-2h4a2 2 0 012 2v12a4 4 0 01-4 4zm0 0h12a2 2 0 002-2v-4a2 2 0 00-2-2h-2.343M11 7.343l1.657-1.657a2 2 0 012.828 0l2.829 2.829a2 2 0 010 2.828l-8.486 8.485M7 17h.01" />
              </svg>
            </div>
            <div class="w-full">
              <label class="text-xs text-gray-600">{{ $t('appearanceProblemQ') }}</label>
              <p class="text-sm text-gray-900">{{ request_info.AppearanceProblem }}</p>
            </div>
          </div>

          <div class="flex items-center gap-3">
            <div class="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center flex-shrink-0">
              <svg class="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
              </svg>
            </div>
            <div class="w-full">
              <label class="text-xs text-gray-600">{{ $t('phoneNoQ') }}</label>
              <p class="text-sm font-medium text-gray-900">{{ request_info.CustomerMobile }}</p>
            </div>
          </div>

          <div v-if="request_info.ExpertOpinion" class="flex items-start gap-3">
            <div class="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center flex-shrink-0">
              <svg class="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
              </svg>
            </div>
            <div class="w-full">
              <label class="text-xs text-gray-600">{{ $t('ExpertOpinionQ') }}</label>
              <p class="text-sm text-gray-900">{{ request_info.ExpertOpinion }}</p>
            </div>
          </div>

          <!-- Predefined Tags -->
          <div v-if="request_info.PredefinedGlobal || request_info.PredefinedChecked" class="flex items-start gap-3">
            <div class="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center flex-shrink-0">
              <svg class="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
              </svg>
            </div>
            <div class="w-full">
              <label class="text-xs text-gray-600 block mb-2">{{ $t('predefinedTagsQ') }}</label>
              <div class="flex flex-wrap gap-2">
                <span 
                  v-for="item in request_info.PredefinedGlobal" 
                  :key="item.Text"
                  class="inline-block bg-blue-50 text-blue-700 text-xs px-3 py-1 rounded-lg border border-blue-200"
                >
                  {{ item.Text }}: {{ item.Value }}
              </span>
                <span 
                  v-for="item in request_info.PredefinedChecked" 
                  :key="item.Text"
                  class="inline-block bg-blue-50 text-blue-700 text-xs px-3 py-1 rounded-lg border border-blue-200"
                >
                  {{ item.Text }}: {{ item.Value }}
                </span>
              </div>
            </div>
          </div>

          <div v-if="request_info.LastTechStatus" class="flex items-start gap-3">
            <div class="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center flex-shrink-0">
              <svg class="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <div class="w-full">
              <label class="text-xs text-gray-600">{{ $t('referDescriptionQ') }}</label>
              <p class="text-sm text-gray-900">{{ request_info.LastTechStatus }}</p>
            </div>
          </div>

          <div v-if="request_info.Summary" class="flex items-start gap-3">
            <div class="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center flex-shrink-0">
              <svg class="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
              </svg>
            </div>
            <div class="w-full">
              <label class="text-xs text-gray-600">{{ $t('descriptionQ') }}</label>
              <p class="text-sm text-gray-900">{{ request_info.Summary }}</p>
            </div>
          </div>

          <div v-if="request_info.TechPriceRange" class="flex items-center gap-3">
            <div class="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center flex-shrink-0">
              <svg class="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <div class="w-full">
              <label class="text-xs text-gray-600">{{ $t('priceRangeQ') }}</label>
              <p class="text-sm font-medium text-gray-900">{{ request_info.TechPriceRange }}</p>
            </div>
          </div>

          <div v-if="request_info.BranchName" class="flex items-center gap-3">
            <div class="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center flex-shrink-0">
              <svg class="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
              </svg>
            </div>
            <div class="w-full">
              <label class="text-xs text-gray-600">{{ $t('branchQ') }}</label>
              <p class="text-sm font-medium text-gray-900">{{ request_info.BranchName }}</p>
            </div>
          </div>

          <div class="flex items-center gap-3">
            <div class="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center flex-shrink-0">
              <svg class="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
              </svg>
            </div>
            <div class="w-full">
              <label class="text-xs text-gray-600">{{ $t('requestType') }}</label>
              <p class="text-sm font-medium text-gray-900">
              <span v-if="request_info.OnSiteService">{{ $t('inBranch') }}</span>
              <span v-else>{{ $t('inLocation') }}</span>
              </p>
            </div>
          </div>
                </div>

              </div>

      <!-- Work Flow Section with Timeline -->
      <div class="bg-white rounded-xl border border-gray-200 p-6">
        <h3 class="text-lg font-bold text-gray-900 mb-6">{{ $t('workFlow') }}</h3>
        <div class="relative">
          <!-- Timeline line -->
          <div class="absolute right-2 top-0 bottom-0 w-0.5 bg-blue-200" style="transform: translateX(50%);"></div>
          
          <!-- Timeline items -->
          <div class="relative space-y-0">
            <div 
              v-for="(item, index) in work_flows"
              :key="index"
              class="relative"
            >
              <!-- Timeline dot -->
              <div class="absolute right-2 top-3 w-3 h-3 bg-blue-600 rounded-full border border-white" style="transform: translateX(50%);"></div>
              
              <!-- Content card -->
              <div class="mr-8 mb-4">
                <div class="bg-gray-50 rounded-lg p-4 border border-gray-200 hover:border-blue-300 transition-all duration-200">
                  <div class="flex items-center justify-between mb-2">
                    <p class="text-sm font-bold text-gray-900">{{ item.StatusName }}</p>
                    <p class="text-xs text-gray-500">{{ item.TranTime }}</p>
                  </div>
                  
                  <div class="grid grid-cols-1 md:grid-cols-2 gap-2 mt-3">
                    <div class="flex items-center gap-2">
                      <svg class="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                      </svg>
                      <span class="text-xs text-gray-600">گیرنده: <span class="font-medium text-gray-900">{{ item.ReceiverName }}</span></span>
                </div>

                    <div class="flex items-center gap-2">
                      <svg class="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                      </svg>
                      <span class="text-xs text-gray-600">کاربر: <span class="font-medium text-gray-900">{{ item.UserName }}</span></span>
                    </div>
              </div>

                  <div v-if="item.Summary" class="mt-3 pt-3 border-t border-gray-200">
                    <p class="text-xs text-gray-700">{{ item.Summary }}</p>
                  </div>
                </div>
              </div>
            </div>
        </div>
        </div>
      </div>

      <!-- Action Button -->
      <button 
        v-if="action_button_text != ''"
        @click="go_to_categories()"
        class="w-full bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white py-4 px-6 rounded-xl font-bold transition-all duration-200 flex items-center justify-center gap-2"
      >
        {{ action_button_text }}
        <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
        </svg>
      </button>

    </div>
  </div>
</template>
<script>
  import { bus } from '../../main.js'

  export default {
    props: [
      'passing_data'
    ],
    data () {
      return {
        errors: [],
        request_info: [],
        action_button_text: '',
        request_statuses: [],
        next_action_status: 0,
        branch_id:0,
work_flows:[],
        technician_name: 'hamid mokhtari',
        device_password: 'hamid mokhtari',

      }
    },
watch: {
      selected_province: function () {
        this.get_cities()
      }
    },
mounted () {
      // var passing_data = JSON.parse(localStorage.getItem('request_data'));
      // this.enable_send_from_country = passing_data.on_site_service;
      // this.get_phone_prefix()
      // this.get_device_status()
      // this.get_provinces()
      this.get_request_info()
      this.get_request_work_flow()
      this.get_branch_permission()
    },
    methods: {
      get_request_info () {
        var self = this
        var user_id = JSON.parse(localStorage.getItem('profile')).UserId
        const send_data = {
          ExpertId: user_id,
          RequestId: this.$route.params.rec_id,
        }
        // this.axios.post('/Register' , send_data,{
        this.axios.post('/GetRequestInfoByReqId', send_data, {})
          .then(response => {
            if (response.data.error == 0) {
              this.request_info = response.data.rec
              this.get_device_status()
            }
            // this.$router.push({ path: "/post-login", params: {} });
          }).catch(e => {
          this.errors.push(e)
        })
      },
      get_request_work_flow () {
        var self = this
        var user_id = JSON.parse(localStorage.getItem('profile')).UserId
        const send_data = {
          ExpertId: user_id,
          ReqId: this.$route.params.rec_id,
        }
        // this.axios.post('/Register' , send_data,{
        this.axios.post('/GetRequestStatusFlowById', send_data, {})
          .then(response => {
            if (response.data.error == 0) {
              this.work_flows = response.data.items
            }
            // this.$router.push({ path: "/post-login", params: {} });
          }).catch(e => {
          this.errors.push(e)
        })
      },
      add_informher_master () {
        var self = this
        var user_id = JSON.parse(localStorage.getItem('profile')).UserId
        const send_data = {
          UserId: user_id,
          RequestId: this.$route.params.rec_id,
          ReqName: this.request_info.RequestName,
          BranchId: this.branch_id,
        }
        // this.axios.post('/Register' , send_data,{
        this.axios.post('/AddMasterFacator', send_data, {})
          .then(response => {
            if (response.data.error == 0) {
this.get_informher_master()
            }
            // this.$router.push({ path: "/post-login", params: {} });
          }).catch(e => {
          this.errors.push(e)
        })
      },
      go_to_categories () {
        var self = this
        if (this.$t('delivered').request_info.StatusId == self.request_statuses[2]) {
          self.action_button_text = self.$t('start_to_delivery')
          self.next_action_status = self.request_statuses[3]
        } else if (self.request_info.StatusId == self.request_statuses[3]) {
          self.action_button_text = self.$t('delivered')
          self.next_action_status = self.request_statuses[4]
        } else if (self.request_info.StatusId == self.request_statuses[4]) {
          self.action_button_text = ''
          self.next_action_status = 0
        } else if (self.request_info.StatusId == self.request_statuses[5]) {
          self.action_button_text = ''
          self.next_action_status = 0
        } else {
          self.action_button_text = ''
          self.next_action_status = 0
        }
        self.change_request_status(this.next_action_status)
      },
      go_to_goods_factors () {
        var self = this
        self.$router.push({ path: '/report-request/request-goods-factors/' + self.$route.params.rec_id })
      },
      get_device_status () {
        var self = this
        var user_id = JSON.parse(localStorage.getItem('profile')).UserId
        console.log()
        const send_data = {
          UserId: user_id,
        }
        // this.axios.post('/Register' , send_data,{
        this.axios.post('/GetStatusSetting', send_data, {})
          .then(response => {
            self.request_statuses = response.data.rslt

            if (self.request_info.StatusId == self.request_statuses[2]) {
              self.action_button_text = self.$t('start_to_delivery')
              self.next_action_status = self.request_statuses[3]
            } else if (self.request_info.StatusId == self.request_statuses[3]) {
              self.action_button_text = self.$t('delivered')
              self.next_action_status = self.request_statuses[4]
            } else if (self.request_info.StatusId == self.request_statuses[4]) {
              self.action_button_text = ''
              self.next_action_status = 0
            } else if (self.request_info.StatusId == self.request_statuses[5]) {
              self.action_button_text = ''
              self.next_action_status = 0
            } else {
              self.action_button_text = ''
              self.next_action_status = 0
            }
          }).catch(e => {
          this.errors.push(e)
        })
      },
      change_request_status (status_id) {
        var self = this
        var user_id = JSON.parse(localStorage.getItem('profile')).UserId
        const send_data = {
          UserId: user_id,
          ReqId: this.$route.params.rec_id,
          Desc: '',
          StatusId: status_id,
        }
        // this.axios.post('/Register' , send_data,{
        this.axios.post('/ChangeRequestStatus', send_data, {})
          .then(response => {
            self.get_request_info()
            if (status_id == self.request_statuses[4]) {
              // GetSignature.open(getActivity(), req_id, "5", 0, "", "0", 0);
              self.$router.push({ name: 'signature', params: { rec_id: self.$route.params.rec_id, type_id: 5 } })
            }
          }).catch(e => {
          this.errors.push(e)
        })
      },
      get_branch_permission () {
        var self = this
        var user_id = JSON.parse(localStorage.getItem('profile')).UserId
        var party_id = localStorage.getItem('party_id')

        const send_data = {
          UserId: user_id,
          Role: 'BranchDefinition',
          PartyId: party_id,
        }
        // this.axios.post('/Register' , send_data,{
        this.axios.post('/ZkGetUserPermission', send_data, {})
          .then(response => {
            if (response.data.error == 0) {
              this.branch_id = response.data.UserBranchId
            }
          }).catch(e => {
          this.errors.push(e)
        })
      },

    }
  }
</script>
